
/**
 * Bia Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class BiaService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message || 'Bia listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bia list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch bia');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bia oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create bia');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bia güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update bia');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Bia silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete bia');
        }
    }

    /**
     * Batch delete items via /batch-delete endpoint
     * @param {Array<string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete bias');
        }
    }

    /**
     * Bulk delete items (legacy — prefer batchDelete)
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Get Stats
     */
    async getStats() {
        try {
            const response = await this.api.getStats();
            return response.data;
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch BIA stats');
        }
    }

    /**
     * Get related processes for a BIA (paginated)
     * @param {string} id - BIA ID
     * @param {Object} params - Query params (page, limit, search, sortBy, sortOrder)
     * @returns {Promise<Object>}
     */
    async getRelatedProcesses(id, params = {}) {
        try {
            const response = await this.api.getRelatedProcesses(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related processes fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related processes');
        }
    }

    /**
     * Link processes to a BIA
     * @param {string} id - BIA ID
     * @param {Array<string>} processIds - Process IDs to link
     * @returns {Promise<Object>}
     */
    async addProcesses(id, processIds) {
        try {
            const response = await this.api.addProcesses(id, processIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Processes linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link processes');
        }
    }

    /**
     * Unlink a process from a BIA
     * @param {string} id - BIA ID
     * @param {string} processId - Process ID to unlink
     * @returns {Promise<Object>}
     */
    async removeProcess(id, processId) {
        try {
            const response = await this.api.removeProcess(id, processId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Process unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink process');
        }
    }

    /**
     * Get related assets for a BIA (paginated)
     * @param {string} id - BIA ID
     * @param {Object} params - Query params (page, limit, search, sortBy, sortOrder)
     * @returns {Promise<Object>}
     */
    async getRelatedAssets(id, params = {}) {
        try {
            const response = await this.api.getRelatedAssets(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related assets fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related assets');
        }
    }

    /**
     * Link assets to a BIA
     * @param {string} id - BIA ID
     * @param {Array<string>} assetIds - Asset IDs to link
     * @returns {Promise<Object>}
     */
    async addAssets(id, assetIds) {
        try {
            const response = await this.api.addAssets(id, assetIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Assets linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assets');
        }
    }

    /**
     * Unlink an asset from a BIA
     * @param {string} id - BIA ID
     * @param {string} assetId - Asset ID to unlink
     * @returns {Promise<Object>}
     */
    async removeAsset(id, assetId) {
        try {
            const response = await this.api.removeAsset(id, assetId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Asset unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink asset');
        }
    }

    /**
     * Get related vendors for a BIA (paginated)
     * @param {string} id - BIA ID
     * @param {Object} params - Query params (page, limit, search, sortBy, sortOrder)
     * @returns {Promise<Object>}
     */
    async getRelatedVendors(id, params = {}) {
        try {
            const response = await this.api.getRelatedVendors(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related vendors fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related vendors');
        }
    }

    /**
     * Link vendors to a BIA
     * @param {string} id - BIA ID
     * @param {Array<string>} vendorIds - Vendor IDs to link
     * @returns {Promise<Object>}
     */
    async addVendors(id, vendorIds) {
        try {
            const response = await this.api.addVendors(id, vendorIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Vendors linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link vendors');
        }
    }

    /**
     * Unlink a vendor from a BIA
     * @param {string} id - BIA ID
     * @param {string} vendorId - Vendor ID to unlink
     * @returns {Promise<Object>}
     */
    async removeVendor(id, vendorId) {
        try {
            const response = await this.api.removeVendor(id, vendorId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Vendor unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink vendor');
        }
    }

    /**
     * Get related recovery resources for a BIA
     * @param {string} id - BIA ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getRelatedRecoveryResources(id, params = {}) {
        try {
            const response = await this.api.getRelatedRecoveryResources(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related recovery resources fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related recovery resources');
        }
    }

    /**
     * Link recovery resources to a BIA
     * @param {string} id - BIA ID
     * @param {Array<string>} recoveryResourceIds - Resource IDs to link
     * @returns {Promise<Object>}
     */
    async addRecoveryResources(id, recoveryResourceIds) {
        try {
            const response = await this.api.addRecoveryResources(id, recoveryResourceIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Recovery resources linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link recovery resources');
        }
    }

    /**
     * Unlink a recovery resource from a BIA
     * @param {string} id - BIA ID
     * @param {string} resourceId - Resource ID to unlink
     * @returns {Promise<Object>}
     */
    async removeRecoveryResource(id, resourceId) {
        try {
            const response = await this.api.removeRecoveryResource(id, resourceId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Recovery resource unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink recovery resource');
        }
    }

    async getRelatedRpoCalculations(id, params = {}) {
        try {
            const response = await this.api.getRelatedRpoCalculations(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related RPO calculations fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related RPO calculations');
        }
    }

    async addRpoCalculations(id, calculationIds) {
        try {
            const response = await this.api.addRpoCalculations(id, calculationIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'RPO calculations linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link RPO calculations');
        }
    }

    async removeRpoCalculation(id, calculationId) {
        try {
            const response = await this.api.removeRpoCalculation(id, calculationId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'RPO calculation unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink RPO calculation');
        }
    }

    async getRelatedRpoRecommendations(id, params = {}) {
        try {
            const response = await this.api.getRelatedRpoRecommendations(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related RPO recommendations fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related RPO recommendations');
        }
    }

    async addRpoRecommendations(id, recommendationIds) {
        try {
            const response = await this.api.addRpoRecommendations(id, recommendationIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'RPO recommendations linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link RPO recommendations');
        }
    }

    async removeRpoRecommendation(id, recommendationId) {
        try {
            const response = await this.api.removeRpoRecommendation(id, recommendationId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'RPO recommendation unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink RPO recommendation');
        }
    }

    /**
     * Get related documents for a BIA (paginated)
     * @param {string} id - BIA ID
     * @param {Object} params - Query params (page, limit, search, sortBy, sortOrder)
     * @returns {Promise<Object>}
     */
    async getRelatedDocuments(id, params = {}) {
        try {
            const response = await this.api.getRelatedDocuments(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related documents fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related documents');
        }
    }

    /**
     * Link documents to a BIA
     * @param {string} id - BIA ID
     * @param {Array<string>} documentIds - Document IDs to link
     * @returns {Promise<Object>}
     */
    async addDocuments(id, documentIds) {
        try {
            const response = await this.api.addDocuments(id, documentIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Documents linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link documents');
        }
    }

    /**
     * Unlink a document from a BIA
     * @param {string} id - BIA ID
     * @param {string} documentId - Document ID to unlink
     * @returns {Promise<Object>}
     */
    async removeDocument(id, documentId) {
        try {
            const response = await this.api.removeDocument(id, documentId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Document unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink document');
        }
    }

    async getRelatedRisks(id, params = {}) {
        try {
            const response = await this.api.getRelatedRisks(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related risks fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related risks');
        }
    }

    async addRisks(id, riskIds) {
        try {
            const response = await this.api.addRisks(id, riskIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Risks linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link risks');
        }
    }

    async removeRisk(id, riskId) {
        try {
            const response = await this.api.removeRisk(id, riskId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Risk unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink risk');
        }
    }

    async getRelatedBcps(id, params = {}) {
        try {
            const response = await this.api.getRelatedBcps(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message || 'Related BCPs fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related BCPs');
        }
    }

    async addBcps(id, bcpIds) {
        try {
            const response = await this.api.addBcps(id, bcpIds);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BCPs linked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link BCPs');
        }
    }

    async removeBcp(id, bcpId) {
        try {
            const response = await this.api.removeBcp(id, bcpId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'BCP unlinked successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink BCP');
        }
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getPartyTemplate(params),
                { maxRetries: 2 },
            );
            return {
                success: true,
                data: response.data,
                message: response.message || 'Party şablonu getirildi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to load party template');
        }
    }

    async getParty(id) {
        try {
            const response = await withRetry(
                () => this.api.getParty(id),
                { maxRetries: 2 },
            );
            return {
                success: true,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch party instance');
        }
    }

    async syncParty(id, body) {
        try {
            const response = await this.api.syncParty(id, body);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Sahiplik ve roller kaydedildi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync party');
        }
    }

    async submit(id) {
        try {
            const response = await this.api.submit(id);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Kayıt onaya gönderildi',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit BIA');
        }
    }

    async checkBiaIdAvailability(params = {}) {
        try {
            const response = await this.api.checkBiaIdAvailability(params);
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to check BIA ID availability');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
